import { useEffect, useState } from "react";
import { tv } from "tailwind-variants";

import { analytics } from "@/config/analytics";
import { useCareplanOrder } from "@/domains/careplan/hooks/use-careplan-order";
import { useCartStore } from "@/stores";
import { Button, icons } from "@/ui";

const stickyCheckoutBarVariants = tv({
  slots: {
    /*
      Phone only. Sticky rather than fixed: the bar is the last thing in the page flow, so it
      pins to the bottom of the viewport while the plan scrolls and sits above the footer once
      the footer is in view. It slides in once the top quarter of the first product card is on screen.
    */
    root: [
      "sticky inset-x-0 bottom-0 z-10 border-t border-line bg-surface px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] shadow-bar",
      "transition-transform duration-300 ease-out md:hidden",
    ],
    button: "w-full justify-between px-6",
    label: "flex items-center gap-2 whitespace-nowrap",
    skeleton: "h-5 w-16 rounded bg-surface-muted",
  },
  variants: {
    hidden: {
      true: {
        root: "translate-y-full",
      },
      false: {
        root: "translate-y-0",
      },
    },
  },
});

const { button, label, root, skeleton } = stickyCheckoutBarVariants();

/* How much of the cue element must be on screen before the bar slides up. */
const REVEAL_FRACTION = 0.25;

type StickyCheckoutBarProps = {
  /**
    The bar stays off screen until the top quarter of this element has scrolled into view. Pass
    the element itself (from a callback ref), not a ref object, so the bar can start watching as
    soon as the element mounts. `null` means it has not mounted yet; leave it out to skip the gate.
  */
  revealOnce?: HTMLElement | null;
};

const useIsRevealed = (target?: HTMLElement | null) => {
  const [isRevealed, setIsRevealed] = useState(target === undefined);

  useEffect(() => {
    if (target === undefined) {
      setIsRevealed(true);

      return undefined;
    }

    if (target === null) {
      setIsRevealed(false);

      return undefined;
    }

    /*
      A scroll listener rather than an IntersectionObserver: the observer only reports when the
      element crosses a threshold, so a fast scroll that jumps it from below the screen to above
      it never fires. One layout read per frame is cheap enough.
    */
    let frame = 0;

    const update = () => {
      frame = 0;
      const { height, top } = target.getBoundingClientRect();
      setIsRevealed(top + height * REVEAL_FRACTION <= window.innerHeight);
    };

    const schedule = () => {
      if (frame === 0) {
        frame = requestAnimationFrame(update);
      }
    };

    update();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
    };
  }, [target]);

  return isRevealed;
};

export const StickyCheckoutBar = ({ revealOnce }: StickyCheckoutBarProps) => {
  const { isLoading, order } = useCareplanOrder();
  const openCart = useCartStore((state) => {
    return state.openCart;
  });
  const showCart = useCartStore((state) => {
    return state.showCart;
  });
  const isRevealed = useIsRevealed(revealOnce);

  const isEmpty = !!order?.isEmpty;
  const isHidden = showCart || !isRevealed;

  const handleClick = () => {
    analytics.track("careplan_sticky_checkout_clicked");
    openCart();
  };

  const renderAmount = () => {
    if (isLoading) {
      return (
        <>
          <span aria-hidden="true" className={skeleton()} />
          <span className="sr-only">Loading your monthly total</span>
        </>
      );
    }

    if (isEmpty) {
      return <span className="text-base whitespace-nowrap">Nothing in your order</span>;
    }

    if (!order) {
      return null;
    }

    return <span>{`$${order.totalAsString}/mo`}</span>;
  };

  return (
    <div aria-hidden={isHidden || undefined} className={root({ hidden: isHidden })}>
      <Button
        className={button()}
        disabled={isEmpty}
        onClick={handleClick}
        size="lg"
        tabIndex={isHidden ? -1 : undefined}
      >
        <span className={label()}>
          <span>View Cart</span>
          <icons.ArrowRight aria-hidden="true" className="size-[18px]" />
        </span>
        {renderAmount()}
      </Button>
    </div>
  );
};
